import { useCallback, useMemo, useState } from 'react';

import { FindPlayersPhotoResponse } from 'utils/api/dto';

import {
    getEliteProspectId,
    parseEliteProspectPhotos,
    PhotosByEliteProspectId,
    PlayerPhotoSource,
    resolvePlayerPhotoUrl,
    resolveScoutPlayerPhotoUrl
} from 'utils/helpers/player-profile-utils';
import { useEliteProspectPhotosRequest } from 'utils/hooks/use-elite-prospect-photos-request';

const NO_PHOTOS: PhotosByEliteProspectId = {};

export type UseEliteProspectPhotosParams = {
    players: ReadonlyArray<PlayerPhotoSource | null | undefined> | null | undefined;
};

/** The Elite Prospects ids of the players without their own photo, without repeats */
export const buildMissingPhotoIds = (players: UseEliteProspectPhotosParams['players']) => {
    const ids = (players || [])
        .filter(player => player && !resolvePlayerPhotoUrl(player.photoHref))
        .map(getEliteProspectId)
        .filter((id): id is number => id != null);

    return Array.from(new Set(ids));
};

type PhotoCache = {
    photos: PhotosByEliteProspectId;
    /** asked already: with a photo or without one */
    checkedIds: ReadonlySet<number>;
};

const EMPTY_CACHE: PhotoCache = { photos: NO_PHOTOS, checkedIds: new Set() };

/**
 * Photos of the players from Elite Prospects: one request for the players without their own photo whose Elite
 * Prospects photo was not asked yet. getPhotoUrl(player) gives the player's photo, else the Elite Prospects one, else
 * null (PlayerHeadshot shows the initials). The photos stay while the players change (the search dropdown changes
 * them on every query): a player seen once is not asked again and does not lose the photo meanwhile.
 */
export const useEliteProspectPhotos = ({ players }: UseEliteProspectPhotosParams) => {
    const [cache, setCache] = useState<PhotoCache>(EMPTY_CACHE);
    const ids = useMemo(() => buildMissingPhotoIds(players), [players]);
    const idsToAsk = useMemo(() => ids.filter(id => !cache.checkedIds.has(id)), [ids, cache.checkedIds]);

    const handleLoaded = useCallback((askedIds: number[], data: FindPlayersPhotoResponse[]) => {
        setCache(current => ({
            photos: { ...current.photos, ...parseEliteProspectPhotos(data) },
            checkedIds: new Set([...Array.from(current.checkedIds), ...askedIds])
        }));
    }, []);

    const { processing } = useEliteProspectPhotosRequest({ ids: idsToAsk, onLoaded: handleLoaded });
    const photosByEliteProspectId = cache.photos;

    const getPhotoUrl = useCallback(
        (player: PlayerPhotoSource | null | undefined) => resolveScoutPlayerPhotoUrl(player, photosByEliteProspectId),
        [photosByEliteProspectId]
    );

    return { photosByEliteProspectId, getPhotoUrl, processing };
};
